import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useState } from "react";
import Input from "components/shared/inputs/input";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import { useCreateVatMutation, useUpdateVatMutation } from "store/api";

export type DatasourceMutationRequest = {
  url: string;
  method: string;
};

type EditableTvaRow = {
  id?: number | string;
  rate?: number | string;
};

type TvaPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  envNumeric: number | undefined;
  mode?: "create" | "edit";
  row?: EditableTvaRow | null;
  onSaved?: () => Promise<void> | void;
};

const TvaPopup: React.FC<TvaPopupProps> = ({
  isOpen,
  onClose,
  request,
  envNumeric,
  mode = "create",
  row,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const isEditMode = mode === "edit";
  const [createVat] = useCreateVatMutation();
  const [updateVat] = useUpdateVatMutation();

  const { control, reset, getValues } = useCustomForm<{ rate: string }>({
    defaultValues: { rate: '0' },
  });

  useEffect(() => {
    setIsSaving(false);
    reset({ rate: isOpen ? String(row?.rate ?? '0') : '0' });
  }, [isOpen, row?.rate, reset]);

  const handleSave = async () => {
    if (!request) return;

    const { rate } = getValues();
    const parsedRate = Number.parseFloat(String(rate).replace(",", "."));

    setIsSaving(true);
    try {
      const baseVatUrl = request.url.replace(/\/[^/]+$/, '');
      const createUrl = baseVatUrl;
      let updateUrl = baseVatUrl;
      if (row?.id != null) {
        updateUrl = `${baseVatUrl}/${row.id}`;
      }

      if (isEditMode) {
        await updateVat({
          url: updateUrl,
          method: 'PUT',
          data: { ...row, rate: parsedRate },
        }).unwrap();
      } else {
        await createVat({
          url: createUrl,
          method: 'POST',
          rate: parsedRate,
          env: envNumeric!,
        }).unwrap();
      }

      onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={isEditMode ? "Modifier TVA" : "Nouvelle TVA"}
      onClose={onClose}
      portal
      fixSize="middle"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t('Back')}
            </Button>
            <Button
              type="button"
              primary
              onClick={handleSave}
            >
              {t('Update')}
            </Button>
          </group>
        ),
      }}
    >
        <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  className="tva-rate-input"
                  inputClassName="tva-rate-number"
                  dataLength="autofit"
                  label={"Rate"}
                  name="rate"
                  control={control}
                  type="number"
                  size="large"
                  step={0.1}
                  min={0}
                  // max={100}
                  controls={false}
                  suffix="%"
                  style={{ direction: "ltr" }}
                />
              </group>
            </group>
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default TvaPopup;